import { useState } from 'react'
import { useForm, useFieldArray, type Control, type UseFormRegister } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FileText, Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { updateEndpoint, type Endpoint } from '@/api/endpoints'
import { ApiError } from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'

// ── Schema ─────────────────────────────────────────────────────────────────

const paramDocSchema = z.object({
  key: z.string(),
  example: z.string(),
  description: z.string().optional(),
})

const errorDocSchema = z.object({
  status: z.coerce.number().int().min(100).max(599),
  description: z.string(),
})

const formSchema = z.object({
  headers: z.array(paramDocSchema),
  query: z.array(paramDocSchema),
  path_params: z.array(paramDocSchema),
  body_example: z.string().optional(),
  body_description: z.string().optional(),
  errors: z.array(errorDocSchema),
})

type FormValues = z.infer<typeof formSchema>

function emptyParam() {
  return { key: '', example: '', description: '' }
}

function emptyError() {
  return { status: 400, description: '' }
}

// ── Param list editor (headers / query / path_params) ──────────────────────

interface ParamListEditorProps {
  title: string
  name: 'headers' | 'query' | 'path_params'
  control: Control<FormValues>
  register: UseFormRegister<FormValues>
}

function ParamListEditor({ title, name, control, register }: ParamListEditorProps) {
  const { t } = useTranslation()
  const { fields, append, remove } = useFieldArray({ control, name })

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{title}</h4>
      {fields.map((field, i) => (
        <div key={field.id} className="grid grid-cols-[1fr_1fr_1.5fr_auto] gap-1.5 items-start">
          <Input placeholder={t('endpoint.requestDocs.colKey')} className="h-8 text-xs font-mono" {...register(`${name}.${i}.key`)} />
          <Input placeholder={t('endpoint.requestDocs.colExample')} className="h-8 text-xs font-mono" {...register(`${name}.${i}.example`)} />
          <Input placeholder={t('endpoint.requestDocs.colDescription')} className="h-8 text-xs" {...register(`${name}.${i}.description`)} />
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive" onClick={() => remove(i)}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => append(emptyParam())}>
        <Plus className="h-3 w-3" />
        {t('endpoint.requestDocs.addRow')}
      </Button>
    </div>
  )
}

// ── Error list editor ───────────────────────────────────────────────────────

function ErrorListEditor({ control, register }: { control: Control<FormValues>; register: UseFormRegister<FormValues> }) {
  const { t } = useTranslation()
  const { fields, append, remove } = useFieldArray({ control, name: 'errors' })

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t('endpoint.requestDocs.errorsTitle')}</h4>
      {fields.map((field, i) => (
        <div key={field.id} className="grid grid-cols-[5rem_1fr_auto] gap-1.5 items-start">
          <Input type="number" placeholder={t('endpoint.requestDocs.colStatus')} className="h-8 text-xs font-mono" {...register(`errors.${i}.status`)} />
          <Input placeholder={t('endpoint.requestDocs.colDescription')} className="h-8 text-xs" {...register(`errors.${i}.description`)} />
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive" onClick={() => remove(i)}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => append(emptyError())}>
        <Plus className="h-3 w-3" />
        {t('endpoint.requestDocs.addRow')}
      </Button>
    </div>
  )
}

// ── Main dialog ──────────────────────────────────────────────────────────────

interface RequestDocsEditorProps {
  endpoint: Endpoint
  projectId: string
}

export function RequestDocsEditor({ endpoint, projectId }: RequestDocsEditorProps) {
  const [open, setOpen] = useState(false)
  const [serverError, setServerError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  const { register, handleSubmit, control, reset, formState: { isSubmitting } } =
    useForm<FormValues>({
      resolver: zodResolver(formSchema),
      defaultValues: {
        headers: endpoint.request_docs.headers.length > 0 ? endpoint.request_docs.headers : [],
        query: endpoint.request_docs.query,
        path_params: endpoint.request_docs.path_params,
        body_example: endpoint.request_docs.body_example ?? '',
        body_description: endpoint.request_docs.body_description ?? '',
        errors: endpoint.request_docs.errors,
      },
    })

  const mutation = useMutation({
    mutationFn: (data: FormValues) =>
      updateEndpoint(endpoint.id, {
        request_docs: {
          headers: data.headers.filter((r) => r.key.trim() !== ''),
          query: data.query.filter((r) => r.key.trim() !== ''),
          path_params: data.path_params.filter((r) => r.key.trim() !== ''),
          body_example: data.body_example?.trim() ? data.body_example : null,
          body_description: data.body_description?.trim() ? data.body_description : null,
          errors: data.errors.filter((e) => e.description.trim() !== ''),
        },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['endpoints', projectId] })
      setOpen(false)
    },
    onError: (err) => {
      setServerError(err instanceof ApiError ? err.message : t('endpoint.requestDocs.saveFailed'))
    },
  })

  const handleOpenChange = (v: boolean) => {
    setOpen(v)
    if (!v) { reset(); setServerError(null) }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1.5">
          <FileText className="h-3.5 w-3.5" />
          {t('endpoint.requestDocs.editButton')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('endpoint.requestDocs.editTitle')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit((d) => void mutation.mutateAsync(d))} className="space-y-5">
          {serverError && (
            <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {serverError}
            </p>
          )}

          <ParamListEditor title={t('endpoint.requestDocs.headersTitle')} name="headers" control={control} register={register} />
          <ParamListEditor title={t('endpoint.requestDocs.queryTitle')} name="query" control={control} register={register} />
          <ParamListEditor title={t('endpoint.requestDocs.pathParamsTitle')} name="path_params" control={control} register={register} />

          <div className="space-y-2">
            <h4 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t('endpoint.requestDocs.bodyTitle')}</h4>
            <div className="space-y-1.5">
              <Label htmlFor="body_description" className="text-xs">{t('endpoint.requestDocs.colDescription')}</Label>
              <Input id="body_description" className="h-8 text-xs" {...register('body_description')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="body_example" className="text-xs">{t('endpoint.requestDocs.colExample')}</Label>
              <Textarea id="body_example" rows={4} className="font-mono text-xs" placeholder={'{\n  "key": "value"\n}'} {...register('body_example')} />
            </div>
          </div>

          <ErrorListEditor control={control} register={register} />

          <DialogFooter>
            <Button type="submit" disabled={isSubmitting || mutation.isPending}>
              {mutation.isPending ? t('common.updating') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
